import { ModuleType, type Building, type Module, type PatchBuildingByIdRequestBody } from "@eisox/api/core-api";
import dayjs, { type Dayjs } from "@eisox/dayjs";
import { z } from "@eisox/zod";

import {
  DEFAULT_MID_SEASON_DELAY,
  DEFAULT_MID_SEASON_HEATING_TEMPERATURE,
  DEFAULT_MID_SEASON_NO_HEATING_TEMPERATURE,
} from "~/constants/season.constant";
import type { EnhancedGateway } from "~/types/gateway.type";

export enum Mode {
  SUMMER = "summer",
  WINTER = "winter",
  SUMMER_WINTER = "summerWinter",
  SUMMER_WINTER_WITH_HALF_SEASON = "summerWinterWithHalfSeason",
}

export interface SummerSeasonType {
  mode: Mode.SUMMER;
}

export interface WinterSeasonType {
  mode: Mode.WINTER;
}

export interface SummerWinterSeasonType {
  mode: Mode.SUMMER_WINTER;
  summerPeriods: {
    begin: string;
    end: string;
  };
  winterPeriods: {
    begin: string;
    end: string;
  };
}

export interface SummerWinterWithHalfSeasonType {
  mode: Mode.SUMMER_WINTER_WITH_HALF_SEASON;
  summerPeriods: {
    begin: string;
    end: string;
  };
  winterPeriods: {
    begin: string;
    end: string;
  };
  midSeason: {
    heatingTemperature: number;
    noHeatingTemperature: number;
    delay: number;
  };
}

export type SeasonType = SummerSeasonType | WinterSeasonType | SummerWinterSeasonType | SummerWinterWithHalfSeasonType;

export const isSummerWinterWithHalfSeasonAvailable = (
  gateways: EnhancedGateway[],
  modules: Pick<Module, "gatewayId" | "type">[],
) => {
  const moduleGatways = gateways.filter(g =>
    modules
      .filter(m =>
        [ModuleType.ECLYPSE_BOILER_ROOM as string, ModuleType.NIAGARA_BOILER_ROOM as string].includes(m.type),
      )
      .map(m => m.gatewayId)
      .includes(g.id),
  );
  return moduleGatways.length > 0;
};

const isSummerMode = (summerModePeriod: Building["summerModePeriod"]) =>
  summerModePeriod?.begin === "01-01" && summerModePeriod.end === "31-12";

const isWinterMode = (winterModePeriod: Building["winterModePeriod"]) =>
  winterModePeriod?.begin === "01-01" && winterModePeriod.end === "31-12";

const addOneDay = (date: string) => {
  const [day = "01", month = "01"] = date.split("-");
  const longMonths = ["01", "03", "05", "07", "08", "10", "12"];
  const shortMonths = ["04", "06", "09", "11"];
  const newDay = Number(day) + 1;
  if (month === "02" && (newDay === 29 || newDay === 30)) return "01-03";
  if (longMonths.includes(month) && newDay === 32) return `01-${(Number(month) + 1).toString().padStart(2, "0")}`;
  if (shortMonths.includes(month) && newDay === 31) return `01-${(Number(month) + 1).toString().padStart(2, "0")}`;
  return `${newDay.toString().padStart(2, "0")}-${month}`;
};

export const isSummerWinterMode = (
  summerModePeriod: NonNullable<Building["summerModePeriod"]>,
  winterModePeriod: NonNullable<Building["winterModePeriod"]>,
) =>
  // oxlint-disable-next-line typescript/no-unnecessary-condition
  winterModePeriod?.end &&
  // oxlint-disable-next-line typescript/no-unnecessary-condition
  summerModePeriod?.end &&
  summerModePeriod.begin === addOneDay(winterModePeriod.end) &&
  addOneDay(summerModePeriod.end) === winterModePeriod.begin;

const formatDateForWebApp = (date: string) => date.replace("-", "/");
const formatDateForBackend = (date: string) => date.replace("/", "-");

export const getInitialState = (house: Building): SeasonType => {
  if (isSummerMode(house.summerModePeriod)) return { mode: Mode.SUMMER };
  if (isWinterMode(house.winterModePeriod)) return { mode: Mode.WINTER };
  if (
    house.summerModePeriod?.begin &&
    house.summerModePeriod.end &&
    house.winterModePeriod?.begin &&
    house.winterModePeriod.end
  ) {
    if (isSummerWinterMode(house.summerModePeriod, house.winterModePeriod)) {
      return {
        mode: Mode.SUMMER_WINTER,
        summerPeriods: {
          begin: formatDateForWebApp(house.summerModePeriod.begin),
          end: formatDateForWebApp(house.summerModePeriod.end),
        },
        winterPeriods: {
          begin: formatDateForWebApp(house.winterModePeriod.begin),
          end: formatDateForWebApp(house.winterModePeriod.end),
        },
      };
    }
    return {
      mode: Mode.SUMMER_WINTER_WITH_HALF_SEASON,
      summerPeriods: {
        begin: formatDateForWebApp(house.summerModePeriod.begin),
        end: formatDateForWebApp(house.summerModePeriod.end),
      },
      winterPeriods: {
        begin: formatDateForWebApp(house.winterModePeriod.begin),
        end: formatDateForWebApp(house.winterModePeriod.end),
      },
      midSeason: {
        heatingTemperature: house.midSeason?.heatingTemperature ?? DEFAULT_MID_SEASON_HEATING_TEMPERATURE,
        noHeatingTemperature: house.midSeason?.noHeatingTemperature ?? DEFAULT_MID_SEASON_NO_HEATING_TEMPERATURE,
        delay: house.midSeason?.tempo ?? DEFAULT_MID_SEASON_DELAY,
      },
    };
  }
  return { mode: Mode.WINTER };
};

export const getPatchBuildingByIdRequestBody = (season: SeasonType): PatchBuildingByIdRequestBody => {
  if (season.mode === Mode.SUMMER_WINTER) {
    return {
      summerModePeriod: {
        begin: formatDateForBackend(season.summerPeriods.begin),
        end: formatDateForBackend(season.summerPeriods.end),
      },
      winterModePeriod: {
        begin: formatDateForBackend(season.winterPeriods.begin),
        end: formatDateForBackend(season.winterPeriods.end),
      },
    };
  }
  if (season.mode === Mode.SUMMER_WINTER_WITH_HALF_SEASON) {
    return {
      summerModePeriod: {
        begin: formatDateForBackend(season.summerPeriods.begin),
        end: formatDateForBackend(season.summerPeriods.end),
      },
      winterModePeriod: {
        begin: formatDateForBackend(season.winterPeriods.begin),
        end: formatDateForBackend(season.winterPeriods.end),
      },
      midSeason: {
        heatingTemperature: season.midSeason.heatingTemperature,
        noHeatingTemperature: season.midSeason.noHeatingTemperature,
        tempo: season.midSeason.delay,
      },
    };
  }
  if (season.mode === Mode.SUMMER) {
    return { summerModePeriod: { begin: "01-01", end: "31-12" } };
  }
  return { winterModePeriod: { begin: "01-01", end: "31-12" } };
};

/** Season boundaries are stored as a day and a month, with no year. */
const SEASON_DATE_FORMAT = "DD-MM";

export const parseSeasonDate = (value: string): Dayjs => dayjs(value, SEASON_DATE_FORMAT);
export const formatSeasonDate = (value: Dayjs): string => value.format(SEASON_DATE_FORMAT);

const isMoreThanOneDay = (endDate: Dayjs, startDate: Dayjs) => endDate.diff(startDate, "day") > 1;
export const displayMidSeasonBetweenEndOfSummerAndStartOfWinter = (summerEnd: string, winterStart: string) =>
  isMoreThanOneDay(parseSeasonDate(winterStart), parseSeasonDate(summerEnd));
export const displayMidSeasonBetweenEndOfWinterAndStartOfSummer = (winterEnd: string, summerStart: string) =>
  isMoreThanOneDay(parseSeasonDate(summerStart), parseSeasonDate(winterEnd));

export const updateSeasonSchema = z
  .object({
    mode: z.enum(Mode),
    summerPeriods: z
      .object({
        begin: z.string(),
        end: z.string(),
      })
      .optional(),
    winterPeriods: z
      .object({
        begin: z.string(),
        end: z.string(),
      })
      .optional(),
    midSeason: z
      .object({
        heatingTemperature: z.number().min(0).max(30),
        noHeatingTemperature: z.number().min(0).max(30),
        delay: z.number().min(0).max(24),
      })
      .optional(),
  })
  .refine(data => {
    if ([Mode.SUMMER_WINTER, Mode.SUMMER_WINTER_WITH_HALF_SEASON].includes(data.mode)) {
      if (
        !data.summerPeriods?.begin ||
        !data.summerPeriods.end ||
        !data.winterPeriods?.begin ||
        !data.winterPeriods.end
      ) {
        return false;
      }
    }
    return true;
  });
